import type { ReactNode } from "react";

import { classes } from "./classes.js";
import { BANK_RADIUS_SM } from "./tokens.js";

/**
 * `F-0c`: the shape of what is coming (prototype `.skel`). A bar is decorative and hidden from a screen reader;
 * `SkeletonRegion` is what says « loading », in the caller's words, once, for the whole region (`role="status"`,
 * `aria-busy`). The shimmer is the theme's and stops under `prefers-reduced-motion`.
 */
export type SkeletonShape = "line" | "title" | "block";

export interface SkeletonProps {
  readonly shape?: SkeletonShape;
  readonly className?: string;
}

const SHAPES: ReadonlyMap<SkeletonShape, string> = new Map<SkeletonShape, string>([
  ["line", "h-3.5 w-full"],
  ["title", "h-5 w-2/5"],
  ["block", "h-[42px] w-full"],
]);

export function Skeleton({ shape = "line", className }: SkeletonProps): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      data-kit="skeleton"
      className={classes(BANK_RADIUS_SM, SHAPES.get(shape), className)}
    />
  );
}

export interface SkeletonRegionProps {
  /** « Chargement… », from the catalogue. */
  readonly label: string;
  readonly children: ReactNode;
  readonly className?: string;
}

export function SkeletonRegion({ label, children, className }: SkeletonRegionProps): React.JSX.Element {
  return (
    <div role="status" aria-busy="true" className={classes("grid gap-2.5", className)}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}
